import React from "react";

export interface FloatingDropdownPanelProps {
  /** When false, nothing renders. */
  isOpen: boolean;
  /** Absolute/fixed positioning style from useFloatingDropdown. */
  style: React.CSSProperties;
  /** Show loading placeholder instead of children. */
  isLoading?: boolean;
  /** Show empty placeholder when no children. */
  isEmpty?: boolean;
  loadingText?: string;
  emptyText?: string;
  /** When provided, fires when the scrollable list reaches near the bottom. */
  onLoadMore?: () => void;
  children: React.ReactNode;
}

/**
 * Floating dropdown shell shared by inline select components. Renders a
 * shadowed popover panel anchored by `style`, scrolls children inside a
 * constrained-height container, and optionally fires `onLoadMore` near
 * the bottom for infinite-scroll lists.
 */
export function FloatingDropdownPanel({
  isOpen,
  style,
  isLoading,
  isEmpty,
  loadingText = "Searching...",
  emptyText = "No results found",
  onLoadMore,
  children,
}: FloatingDropdownPanelProps) {
  if (!isOpen) return null;

  return (
    <div
      className="absolute border rounded-md bg-popover text-popover-foreground shadow-lg overflow-hidden"
      style={style}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div
        className="max-h-[min(60vh,320px)] overflow-y-auto"
        onScroll={
          onLoadMore
            ? (e) => {
                const el = e.currentTarget;
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 64) {
                  onLoadMore();
                }
              }
            : undefined
        }
      >
        {isLoading ? (
          <div className="px-3 py-4 text-sm text-muted-foreground">{loadingText}</div>
        ) : isEmpty ? (
          <div className="px-3 py-4 text-sm text-muted-foreground">{emptyText}</div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
